import React from "react";
import {
  Form,
  FormInput,
  FormRadio,
  Grid,
  GridColumn,
  GridRow,
  Button,
  Modal,
  ModalActions,
  Header,
  Select,
} from "semantic-ui-react";
import { getReviewQuestionCreateTemplate } from "@actions/compliance";

// Type dropdown: one option per question type, using name for display
const buildTypeOptions = (questionTypes = []) =>
  (questionTypes || []).map((qt) => ({
    key: qt.name,
    value: qt.name,
    text: qt.name,
  }));

// Subtype dropdown: options from the selected type's subTypes (name = display, type = value)
const buildSubtypeOptions = (questionTypes = [], selectedTypeName) => {
  const qt = (questionTypes || []).find((t) => t.name === selectedTypeName);
  if (!qt || !qt.subTypes || !qt.subTypes.length) return [];
  return qt.subTypes.map((st) => ({
    key: st.type,
    value: st.type,
    text: st.name,
  }));
};

const getSelectedQuestionType = (questionTypes = [], selectedTypeName) =>
  (questionTypes || []).find((t) => t.name === selectedTypeName);

// For edit: derive type name (and thus subtype) from type value using questionTypes
const getTypeNameFromValue = (questionTypes = [], typeValue) => {
  if (!typeValue) return "";
  for (const qt of questionTypes) {
    if (qt.type === typeValue) return qt.name;
    if (qt.subTypes && qt.subTypes.length) {
      const found = qt.subTypes.some((st) => st.type === typeValue);
      if (found) return qt.name;
    }
  }
  return "";
};

// Type value(s) that identify a Yes/No question (from questionTypes template)
const getYesNoTypeValues = (questionTypes = []) => {
  const yesNo = (questionTypes || []).find((t) => t.name === "Yes/No");
  if (!yesNo) return [];
  if (yesNo.subTypes && yesNo.subTypes.length) {
    return yesNo.subTypes.map((st) => st.type);
  }
  return yesNo.type != null ? [yesNo.type] : [];
};

// Root questions only (no parent), no other child, and Yes/No type only. Exclude current question when editing.
// When editing, include currentParentId in options so the Select can show the existing parent even if it would otherwise be excluded.
const buildValidParentOptions = (
  questions = [],
  currentQuestionId,
  currentParentId,
  questionTypes = [],
) => {
  const list = questions || [];
  const yesNoTypeValues = getYesNoTypeValues(questionTypes);
  const isYesNoQuestion = (q) =>
    yesNoTypeValues.length === 0 || yesNoTypeValues.includes(q.type);
  const idsThatHaveChildren = new Set(
    list
      .map((q) => q.parentQuestionId ?? q.parentId)
      .filter((id) => id != null && id !== "" && Number(id) !== 0)
      .map((id) => Number(id)),
  );
  const rootQuestions = list.filter((q) => {
    const parentId = q.parentQuestionId ?? q.parentId ?? null;
    const hasNoParent =
      parentId == null || parentId === "" || Number(parentId) === 0;
    const isNotSelf =
      currentQuestionId == null || Number(q.id) !== Number(currentQuestionId);
    const hasNoChild = !idsThatHaveChildren.has(Number(q.id));
    return (
      hasNoParent && isNotSelf && hasNoChild && isYesNoQuestion(q)
    );
  });
  const optionIds = new Set(rootQuestions.map((q) => Number(q.id)));
  let options = rootQuestions.map((q) => ({
    key: q.id,
    value: Number(q.id),
    text: q.ordinal,
  }));
  const parentIdNum =
    currentParentId != null && currentParentId !== ""
      ? Number(currentParentId)
      : 0;
  if (parentIdNum > 0 && !optionIds.has(parentIdNum)) {
    const parentQuestion = list.find((q) => Number(q.id) === parentIdNum);
    if (parentQuestion) {
      options = [
        {
          key: parentQuestion.id,
          value: Number(parentQuestion.id),
          text: parentQuestion.ordinal,
        },
        ...options,
      ];
    }
  }
  return options.sort((a, b) => a.value - b.value);
};

class NewReviewQuestionModal extends React.Component {
  state = {
    loading: false,
    templateLoading: true,
    record: null,
    questionTypes: [],
    formData: {
      question: "",
      selectedTypeName: "",
      type: "",
      reviewTypeId: 2,
      parentQuestionId: null,
      parentCondition: null,
      isDisabled: false,
      ordinal: 0,
      requiredField: true,
    },
  };

  componentDidMount() {
    this.loadTemplate();
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.loadTemplate();
    }
    if (this.props.open && this.props.initialData !== prevProps.initialData) {
      this.applyInitialData(this.props.initialData);
    }
  }

  applyInitialData = (initialData) => {
    if (!initialData || !this.state.questionTypes.length) return;
    const { questionTypes } = this.state;
    const selectedTypeName = getTypeNameFromValue(questionTypes, initialData.type);
    const parentId =
      initialData.parentQuestionId ?? initialData.parentId ?? 0;
    const parentCond =
      initialData.parentCondition ?? initialData.condition ?? null;
    this.setState({
      formData: {
        ...this.state.formData,
        id: initialData.id,
        question: initialData.question ?? "",
        selectedTypeName,
        type: initialData.type ?? "",
        reviewTypeId: initialData.reviewTypeId ?? 2,
        parentQuestionId: parentId,
        parentCondition: parentCond,
        isDisabled: initialData.isDisabled ?? false,
        ordinal: initialData.ordinal ?? 0,
        requiredField: initialData.required ?? initialData.requiredField ?? true,
      },
    });
  };

  loadTemplate = async () => {
    this.setState({ templateLoading: true });
    const response = await getReviewQuestionCreateTemplate();
    const { initialData } = this.props;
    if (response) {
      const record = response.record || {};
      const questionTypes = response.questionTypes || [];
      const isEdit = initialData && (initialData.id || initialData.id === 0);
      const nextOrdinal = isEdit ? (record.ordinal ?? 0) : this.getNextOrdinal();
      let formData = {
        question: record.question ?? "",
        selectedTypeName: "",
        type: record.type ?? "",
        reviewTypeId: 2,
        parentQuestionId: record.parentQuestionId ?? 0,
        parentCondition: record.parentCondition ?? null,
        isDisabled: record.isDisabled ?? false,
        ordinal: nextOrdinal,
        requiredField: true,
      };
      if (isEdit && initialData) {
        console.log(initialData);
        formData = {
          ...formData,
          id: initialData.id,
          question: initialData.question ?? "",
          selectedTypeName: getTypeNameFromValue(questionTypes, initialData.type),
          type: initialData.type ?? "",
          parentQuestionId: initialData.parentId ?? 0,
          condition: initialData.condition ?? null,
          isDisabled: initialData.isDisabled ?? false,
          ordinal: initialData.ordinal ?? 0,
          requiredField: initialData.required ?? true,
        };
      }
      this.setState({
        record,
        questionTypes,
        formData,
        templateLoading: false,
      });
    } else {
      this.setState({ templateLoading: false });
    }
  };

  handleChange = (_, { name, value }) => {
    const { questionTypes, formData } = this.state;
    const next = { ...formData, [name]: value };
    if (name === "selectedTypeName") {
      const qt = getSelectedQuestionType(questionTypes, value);
      if (qt && qt.type) {
        next.type = qt.type;
      } else if (qt && qt.subTypes) {
        next.type = "";
      }
    }
    if (
      name === "parentQuestionId" &&
      (value === "" || value == null || Number(value) === 0)
    ) {
      next.condition = null;
    }
    this.setState({ formData: next });
  };

  handleToggle = (_, { name, checked }) => {
    this.setState({
      formData: { ...this.state.formData, [name]: checked },
    });
  };

  getNextOrdinal = () => {
    const { questions = [] } = this.props;
    if (questions.length === 0) return 1;
    const maxOrdinal = Math.max(...questions.map((q) => q.ordinal || 0), 0);
    return maxOrdinal + 1;
  };

  handleSave = async () => {
    const { formData } = this.state;
    if (!formData.question || !formData.type) return;
    this.setState({ loading: true });
    const reviewTypeId = this.props.reviewTypeId ?? formData.reviewTypeId ?? 2;
    const payload = {
      question: formData.question.trim(),
      type: formData.type,
      reviewTypeId,
      parentId:
        formData.parentQuestionId == 0 || formData.parentQuestionId === ""
          ? null
          : Number(formData.parentQuestionId),
      condition:
        formData.condition === "" ? null : formData.condition,
      isDisabled: formData.isDisabled,
      ordinal: formData.ordinal,
      required: formData.requiredField,
    };
    if (formData.id != null) payload.id = formData.id;
    await this.props.onSave(payload);
    this.setState({ loading: false });
    this.props.onClose();
  };

  getValidParentIds = () => {
    const { questions = [] } = this.props;
    const { formData, questionTypes = [] } = this.state;
    return buildValidParentOptions(
      questions,
      formData.id,
      formData.parentQuestionId,
      questionTypes,
    ).map((o) => o.value);
  };

  canSave = () => {
    const { formData } = this.state;
    const parentIdVal =
      formData.parentQuestionId === null || formData.parentQuestionId === ""
        ? 0
        : Number(formData.parentQuestionId);
    const hasParent = parentIdVal > 0;
    const validParentIds = this.getValidParentIds();
    const parentInValidRange =
      !hasParent || validParentIds.includes(parentIdVal);
    const hasValidParentCondition =
      !hasParent ||
      formData.condition === true ||
      formData.condition === false;
    const isEdit = formData.id != null;
    const parentNotSelf = !isEdit || parentIdVal !== Number(formData.id);
    return (
      formData.question &&
      formData.question.trim() &&
      formData.type &&
      parentInValidRange &&
      hasValidParentCondition &&
      parentNotSelf
    );
  };

  render() {
    const { open, onClose, questions = [], initialData } = this.props;
    const { formData, questionTypes, templateLoading } = this.state;
    const isEdit = Boolean(initialData && (initialData.id != null));
    const typeOptions = buildTypeOptions(questionTypes);
    const selectedType = getSelectedQuestionType(
      questionTypes,
      formData.selectedTypeName,
    );
    const showSubtype = selectedType && selectedType.subTypes && selectedType.subTypes.length > 0;
    const subtypeOptions = buildSubtypeOptions(
      questionTypes,
      formData.selectedTypeName,
    );
    const parentOptions = buildValidParentOptions(
      questions,
      isEdit ? formData.id : null,
      isEdit ? formData.parentQuestionId : null,
      questionTypes,
    );
    const parentIdValue =
      formData.parentQuestionId === null || formData.parentQuestionId === ""
        ? 0
        : formData.parentQuestionId;
    const hasParent = Number(parentIdValue) > 0;
    const validParentIds = parentOptions.map((o) => o.value);
    const parentIdInvalid =
      hasParent && !validParentIds.includes(Number(parentIdValue));

    return (
      <Modal open={open} onClose={onClose} closeIcon size="small">
        <Header>{isEdit ? "Edit Question" : "Add a New Question"}</Header>
        <Modal.Content>
          {templateLoading ? (
            <p>Loading...</p>
          ) : (
            <Form>
              <Grid container>
                <GridRow>
                  <GridColumn width={6}>Question *</GridColumn>
                  <GridColumn width={10}>
                    <FormInput
                      value={formData.question}
                      name="question"
                      onChange={this.handleChange}
                      placeholder="Question text"
                      required
                    />
                  </GridColumn>
                </GridRow>
                <GridRow>
                  <GridColumn width={6}>Type *</GridColumn>
                  <GridColumn width={10}>
                    <Select
                      options={typeOptions}
                      value={formData.selectedTypeName}
                      name="selectedTypeName"
                      onChange={this.handleChange}
                      placeholder="Select question type"
                    />
                  </GridColumn>
                </GridRow>
                {showSubtype && (
                  <GridRow>
                    <GridColumn width={6}>Subtype *</GridColumn>
                    <GridColumn width={10}>
                      <Select
                        options={subtypeOptions}
                        value={formData.type}
                        name="type"
                        onChange={this.handleChange}
                        placeholder="Select subtype"
                      />
                    </GridColumn>
                  </GridRow>
                )}
                <GridRow>
                  <GridColumn width={6}>Required Field *</GridColumn>
                  <GridColumn width={10}>
                    <FormRadio
                      toggle
                      name="requiredField"
                      checked={formData.requiredField}
                      onChange={this.handleToggle}
                    />
                  </GridColumn>
                </GridRow>
                <GridRow>
                  <GridColumn width={6}>Hide *</GridColumn>
                  <GridColumn width={10}>
                    <FormRadio
                      toggle
                      name="isDisabled"
                      checked={formData.isDisabled}
                      onChange={this.handleToggle}
                    />
                  </GridColumn>
                </GridRow>
                <GridRow>
                  <GridColumn width={6}>Ordinal</GridColumn>
                  <GridColumn width={10}>
                    <FormInput
                      readOnly
                      value={isEdit ? formData.ordinal : this.getNextOrdinal()}
                    />
                  </GridColumn>
                </GridRow>
                <GridRow>
                  <GridColumn width={6}>
                    Select Parent ID
                  </GridColumn>
                  <GridColumn width={10}>
                    <Form.Field>
                      <Select
                        options={[
                          { key: "none", value: 0, text: "No parent" },
                          ...parentOptions,
                        ]}
                        value={hasParent ? Number(parentIdValue) : 0}
                        name="parentQuestionId"
                        onChange={this.handleChange}
                      />                                    
                    </Form.Field>
                  </GridColumn>
                </GridRow>
                {hasParent && (
                  <GridRow>
                    <GridColumn width={6}>Parent condition *</GridColumn>
                    <GridColumn width={10}>
                      <Form.Field>
                        <Select
                          options={[
                            { key: "yes", value: true, text: "Yes" },
                            { key: "no", value: false, text: "No" },
                          ]}
                          value={formData.condition}
                          name="condition"
                          onChange={this.handleChange}
                        />
                      </Form.Field>
                    </GridColumn>
                  </GridRow>
                )}
              </Grid>
            </Form>
          )}
        </Modal.Content>
        <ModalActions>
          <Button negative onClick={onClose}>
            Cancel
          </Button>
          <Button
            positive
            content="Save"
            icon="save"
            onClick={this.handleSave}
            loading={this.state.loading}
            disabled={!this.canSave() || this.state.loading}
          />
        </ModalActions>
      </Modal>
    );
  }
}

export default NewReviewQuestionModal;
